import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  PAGE, CONTENT_W, COLORS, text, wrapText, drawFooters,
  checkPageBreak, safeText, fmtDate,
} from "../report/pdfHelpers";
import { renderChartToCanvas } from "./chartImageExporter";
import { reportFileName } from "../report/pdfHelpers";
import { LOGO_BASE64_PNG } from "../../assets/logoBase64";

// ============================================================
// CONVERSATION EXPORT
// ============================================================
// Walks session.messages EXACTLY as stored — the same text, chart data,
// and table data already shown on screen. Nothing here is recalculated;
// this is a formatted transcript of the actual conversation, not a fresh
// analysis.

const TITLE = "Conversation Export";
const CHART_IMG_W = CONTENT_W;
const CHART_IMG_H = CHART_IMG_W * 0.64; // matches the 1000x640 canvas aspect ratio

function drawChatHeader(doc, datasetName, watermark = true) {

  doc.setFillColor(...COLORS.brand);
  doc.rect(0, 0, PAGE.w, 16, "F");

  doc.setTextColor(255, 255, 255);

  let brandTextX = PAGE.margin;

  if (watermark) {
    const logoSize = 9;
    const logoY = (16 - logoSize) / 2;
    try {
      doc.addImage(LOGO_BASE64_PNG, "PNG", PAGE.margin, logoY, logoSize, logoSize);
      brandTextX = PAGE.margin + logoSize + 3;
    } catch (err) {
      // fall back to text-only branding if the image ever fails to embed
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    text(doc, "InsightPulse AI", brandTextX, 10.5);
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const right = `${TITLE} - ${datasetName}`;
  const w = doc.getTextWidth(safeText(right));
  text(doc, right, watermark ? PAGE.w - PAGE.margin - w : PAGE.margin, 10.5);

  doc.setTextColor(...COLORS.text);
}

export async function exportConversation(session, { watermark = true } = {}) {

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const datasetName = session.name || "Dataset";
  const messages = session.messages || [];

  // ---- Cover block ----
  doc.setFillColor(...COLORS.brand);
  doc.rect(0, 0, PAGE.w, 50, "F");

  doc.setTextColor(255, 255, 255);

  if (watermark) {
    const logoSize = 12;
    try {
      doc.addImage(LOGO_BASE64_PNG, "PNG", PAGE.margin, 8, logoSize, logoSize);
    } catch (err) {
      // fall back to text-only branding if the image ever fails to embed
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    text(doc, "InsightPulse AI", PAGE.margin + logoSize + 3, 15.5);
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  text(doc, TITLE, PAGE.margin, 32);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  text(doc, datasetName, PAGE.margin, 42);

  doc.setTextColor(...COLORS.text);

  let y = 64;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(...COLORS.subtext);
  text(doc, `Exported ${fmtDate(new Date().toISOString())}  -  ${messages.length} messages`, PAGE.margin, y);
  doc.setTextColor(...COLORS.text);
  y += 12;

  // ---- Walk the conversation in order ----
  for (const msg of messages) {

    y = checkPageBreak(doc, y, 20);

    const isUser = msg.role === "user";
    const label = isUser ? "YOU ASKED" : "INSIGHTPULSE ANSWERED";
    const bodyText = msg.text || msg.content || "";

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...(isUser ? COLORS.brand : COLORS.positive));
    text(doc, label, PAGE.margin, y);
    y += 5;

    doc.setFont("helvetica", isUser ? "bold" : "normal");
    doc.setFontSize(isUser ? 11 : 10);
    doc.setTextColor(...COLORS.text);
    y = wrapText(doc, bodyText, PAGE.margin, y, CONTENT_W, isUser ? 5.5 : 5) + 4;

    // Chart, if this answer had one
    if (msg.chart?.data?.length > 0) {

      y = checkPageBreak(doc, y, CHART_IMG_H + 10, () => drawChatHeader(doc, datasetName, watermark));

      try {

        const canvas = renderChartToCanvas({
          data: msg.chart.data,
          xKey: "label",
          yKey: "value",
          type: msg.chart.type,
        });

        const imgData = canvas.toDataURL("image/png");
        doc.addImage(imgData, "PNG", PAGE.margin, y, CHART_IMG_W, CHART_IMG_H);
        y += CHART_IMG_H + 6;

      } catch (err) {
        console.error("Failed to render chart into export:", err);
      }
    }

    // Table, if this answer had one
    if (msg.table?.rows?.length > 0) {

      y = checkPageBreak(doc, y, 30, () => drawChatHeader(doc, datasetName, watermark));

      autoTable(doc, {
        startY: y,
        head: [msg.table.headers],
        body: msg.table.rows.map(row =>
          row.cells.map(cell => (typeof cell === "number" ? cell.toLocaleString(undefined, { maximumFractionDigits: 2 }) : cell))
        ),
        theme: "grid",
        headStyles: { fillColor: COLORS.brand, fontSize: 8.5 },
        styles: { fontSize: 8.5 },
        margin: { left: PAGE.margin, right: PAGE.margin },
      });

      y = doc.lastAutoTable.finalY + 6;
    }

    doc.setDrawColor(...COLORS.border);
    doc.line(PAGE.margin, y, PAGE.w - PAGE.margin, y);
    y += 8;
  }

  if (!messages.length) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.subtext);
    text(doc, "This conversation has no messages yet.", PAGE.margin, y);
  }

  drawFooters(doc, TITLE, { watermark });

  const pageCount = doc.internal.getNumberOfPages();
  for (let p = 2; p <= pageCount; p++) {
    doc.setPage(p);
    drawChatHeader(doc, datasetName, watermark);
  }

  doc.save(reportFileName("Conversation", datasetName));
}
